import {ApiError, ICartPosition, PaymentFactory} from "@connox/typescript-library";
import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {Trans, useTranslation} from "react-i18next";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import CartDetails from "../../../cart/CartDetails/CartDetails";
import ItemList from "../../../cart/ItemList/ItemList";
import VoucherBlock from "../../../cart/Rebates/VoucherBlock";
import {Button, ButtonStyles, ButtonType} from "../../../lib/form/Button/Button";
import {getShopId} from "../../../lib/getShopId";
import {amazonMerchantId} from "../../../lib/payments/AmazonPayLibrary";
import {IPaymentModule} from "../../../lib/payments/IPaymentModule";
import {INTERNAL_ERROR_TYPE, PaymentController, Payments} from "../../../lib/payments/PaymentController";
import "../../../lib/SimpleRadioList.scss";
import window from "../../../lib/WindowExtended";
import ListLoader from "../../../utils/loader/ListLoader";
import Spinner from "../../../utils/loader/Spinner";
import {CheckoutError, CheckoutStep, DEFAULT_ERROR_MESSAGE, IIsVisible, StringWithAnchorComp} from "../../Checkout";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {useCheckoutViewContext} from "../../CheckoutContextProvider/CheckoutViewContextProvider";
import AnnotationBlock from "./AnnotationBlock";
import SummaryAddress from "./SummaryAddress";
import SummaryNewsletter from "./SummaryNewsletter";
import SummaryPaymentOption from "./SummaryPaymentOption";
import SummaryShippingOption from "./SummaryShippingOption";
import {BraintreeError} from "braintree-web";
import {PossibleNetworkError} from "../../../cart/ShoppingCart/ShoppingCart";
import {cleanUrl} from "../Finished/Finished";
import styles from "./Summary.module.scss";
import {isSameAddress} from "../AddressStep/AddressStep";
import {BraintreeErrorTypes} from "braintree-web/modules/core";
import {captureSentryException} from "../../../../helpers/sentryHelper";
import {updateUrlParams} from "../../../cart/CartIcon/CartIconPortal";

const Summary: React.FC<IIsVisible> = (props) => {
  const {setIsIntoCheckout} = useCartContext();
  const {checkout} = useCheckoutContext();
  const checkoutView = useCheckoutViewContext();
  const {cartData, paymentId} = checkout;
  const [paymentModule, setPaymentModule] = useState<IPaymentModule | null>(
    checkout.paymentId ? PaymentController.createPayment(checkout, checkout.paymentId) : null,
  );
  const {i18n, t} = useTranslation("checkout");
  const itemPosCount = cartData.positions.filter((pos: ICartPosition) => pos.type === "Item" && !!pos.item).length;
  const buttonText = t("Jetzt kaufen");
  const amazonPayError = "AMAZON_PAY_ERROR";
  const btoaError = btoa(amazonPayError);
  const [errors, setErrors] = useState<(string | JSX.Element)[]>([]);
  const previousValuesRef = useRef<{cartAmount: string; voucherPosition?: ICartPosition}>({
    cartAmount: cartData.value.payable.amount,
    voucherPosition: cartData.positions.find((pos) => pos.type === "Voucher"),
  });
  const [isAmazonPayButtonEnabled, setIsAmazonPayButtonEnabled] = useState(window.isPreauthorizingDone);

  const restrictingPayments = [Payments.AmazonPay, Payments.PaypalExpressBraintree, Payments.CreditCardBraintree];
  const isCartManipulationAllowed = paymentId && !restrictingPayments.includes(paymentId);

  const purchaseButtonErrors = (
    err: ApiError | BraintreeError | Error,
    occurence: string,
    additional?: Record<string, unknown>,
  ) => {
    const sections = document.querySelectorAll("#ModalRootElement > section");
    if (sections.length) {
      sections.forEach((section) => (section.scrollTop = 0));
    }

    const BTOrDefaultError: Error | IBraintreeError = ((err as ApiError).originalError || err) as Error;

    let errorMessage: string[] = [BTOrDefaultError.message];
    if (
      [
        "PostOffice or PackingStation not allowed as invoice address",
        "delivery to packing station not available for logistics type.",
        "Phone number required",
      ].includes(errorMessage[0])
    ) {
      // errors regarding amazon pay (checkout.messages[])
      setErrors([<StringWithAnchorComp key={errorMessage[0].split(" ")[0]} message={errorMessage[0]} />]);

      return;
    }

    const braintreeOrNetworkError = BTOrDefaultError as IBraintreeError & PossibleNetworkError;

    /* It's possibly a network error if BraintreeError.type does not exist */
    if (!braintreeOrNetworkError.type) {
      const networkErrorMessages = braintreeOrNetworkError.response?.data?.messages;
      if (networkErrorMessages && networkErrorMessages.length) {
        errorMessage = networkErrorMessages.map((msg) => msg.message);
      }
    } else if ("details" in braintreeOrNetworkError) {
      try {
        errorMessage =
          braintreeOrNetworkError.details.originalError.details.originalError?.error.message.split("\u000A") ??
          errorMessage;
      } catch (e) {
        errorMessage = braintreeOrNetworkError.details.originalError.error.message.split("\u000A") ?? errorMessage;
      }
      if (
        errorMessage.length > 1 &&
        errorMessage.includes("Billing line1 format is invalid.") &&
        errorMessage.includes("Shipping line1 format is invalid.") &&
        isSameAddress(checkout.invoiceAddress, checkout.deliveryAddress)
      ) {
        errorMessage.splice(errorMessage.indexOf("Billing line1 format is invalid."), 1);
        errorMessage.splice(errorMessage.indexOf("Shipping line1 format is invalid."), 1);
        errorMessage.push("Shipping/Billing line1 format is invalid.");
      }
    }
    /* else, just keep "errorMessage = [BTOrDefaultError.message]", as stated above */

    setErrors(() =>
      errorMessage.map((msg) => {
        // if error is unknown show a default error msg and capture a sentry exception
        if (!i18n.exists(msg, {ns: "checkout"})) {
          const sentryEventId = captureSentryException(
            BTOrDefaultError,
            checkout,
            occurence || "Summary Purchase Button",
            additional,
          );

          return t(DEFAULT_ERROR_MESSAGE, {sentryEventId});
        }

        // If APay addresses cannot be found.. Should
        // never be the case! just for debugging purposes
        if (msg === "[AmazonPayReadableWidget] 404: File not found!") {
          // should not be the case! just for debugging purposes
          captureSentryException(err, checkout, "Amazon Pay readable address widget");
        }

        // error is known and most likely user is supposed to act accordingly
        return t(msg);
      }),
    );
  };

  /**
   * set payment module according to payment id if set
   */
  useEffect(() => {
    if (paymentId === Payments.PaypalExpressBraintree || paymentId === Payments.AmazonPay) {
      return;
    }
    if (paymentId) {
      setPaymentModule(PaymentController.createPayment(checkout, paymentId));
    }
  }, [paymentId, props.isVisible]);

  /**
   * do initial things when view is visible and payment module is set
   */
  useEffect(() => {
    // If either one is not set, something is wrong here
    if (!(props.isVisible && paymentModule && paymentId)) {
      return;
    }

    // All payment types but APay
    if (paymentId !== Payments.AmazonPay) {
      // Nothing to then but only to catch
      void paymentModule.onEnterSummary().catch((err: Error) => {
        // Has purchaseButtonErrors() already been called by onError callback in PaypalModule
        if (
          err.name.startsWith(INTERNAL_ERROR_TYPE.PAYPAL.OnError) ||
          err.name.startsWith(INTERNAL_ERROR_TYPE.PAYPAL.OnErrorNoHandle)
        ) {
          return;
        }
        err.name = INTERNAL_ERROR_TYPE.PAYPAL.OnCatch + err.name;
        purchaseButtonErrors(err, "on Enter Summary");
      });

      return;
    }

    // APay, but only if not already into it
    if (
      window.isIntoPreauthorizing ||
      window.isPreauthorizingDone ||
      new URLSearchParams(window.location.search).get("AuthenticationStatus")
    ) {
      return;
    }

    // Now we're into it
    window.isIntoPreauthorizing = true;

    /* APay init */
    void PaymentFactory.initPayment(`${getShopId()}`, checkout.checkoutId, window.orderReferenceId)
      .then(async (transaction) => {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        /* Apay init confimation flow */
        /* AND CALLBACK */
        window.paymentTransactionId = transaction.paymentTransactionId;
        window.OffAmazonPayments.initConfirmationFlow(
          amazonMerchantId,
          window.orderReferenceId,

          async (confirmationFlow): Promise<void> => {
            const {origin, pathname, search} = window.location;
            const usp = new URLSearchParams(search);

            /* Setting URL param for success and error */
            /* On reload checkout will automatically open up and customer is redirected to summary again */
            usp.set("gotoStep", CheckoutStep.SUMMARY);
            const successUrl = origin + pathname + "?" + usp.toString();
            usp.set("error", btoaError);
            const failureUrl = origin + pathname + "?" + usp.toString();

            if (!window.paymentTransactionId) {
              window.paymentTransactionId = checkout.paymentTransaction?.paymentTransactionId || "";
            }
            if (!window.paymentTransactionId) {
              purchaseButtonErrors(
                new Error("Der Kauf konnte nicht initialisiert werden. Bitte wiederholen Sie den Kaufprozess"),
                "Amazon Pay Purchase preautorize (before redir)",
              );
              confirmationFlow.error();

              return;
            }

            /* Apay pre-authorize */

            try {
              await PaymentFactory.amazonPayPreAuthorize(window.paymentTransactionId, successUrl, failureUrl);
              /* get current checkout and check if pt state is still 'created' */
              await new Promise((resolve) => setTimeout(resolve, 1000));
              await checkout.get();
              window.isPreauthorizingDone = true;
              window.isIntoPreauthorizing = false;

              const isTransactionStateCreated = checkout.paymentTransaction?.state === "created";

              const isPackingStationError = checkout.messages?.some(
                (msg) => msg.message === "delivery to packing station not available for logistics type.",
              );

              const isPostOfficeError = checkout.messages?.some(
                (msg) => msg.message === "delivery to post office not available for logistics type.",
              );

              const isWrongInvoiceAddressError = checkout.messages?.some(
                (msg) => msg.message === "PostOffice or PackingStation not allowed as invoice address",
              );

              const isPhoneNumberNeeded =
                checkoutView.shippingOptions.find((so) => so.logisticsTypeId === checkout.logisticsTypeId)
                  ?.isPhoneRequired && !checkout.deliveryAddress?.phone;

              const isDeliveryAddressNotValid = checkout.messages.some(
                (msg) => msg.code === "DELIVERY_ADDRESS_NOT_VALID",
              );

              const isInvoiceAddressNotValid = checkout.messages.some(
                (msg) => msg.code === "INVOICE_ADDRESS_NOT_VALID",
              );

              let tempError: Error | null = null;

              // Thrown errors with known i18n translation will be visualized to the customer
              switch (true) {
                case !checkout.paymentTransaction:
                  // Shouldn't be the case. Will be reported to Sentry
                  tempError = new Error("Amazon Pay preauthorize: No paymentTransaction object present in checkout");
                  break;
                case !isTransactionStateCreated:
                  // Shouldn't be the case. Will be reported to Sentry
                  tempError = new Error("Amazon Pay preauthorize not successful");
                  break;
                case isPackingStationError:
                  tempError = new Error("delivery to packing station not available for logistics type.");
                  break;
                case isPostOfficeError:
                  tempError = new Error("delivery to post office not available for logistics type.");
                  break;
                case isWrongInvoiceAddressError:
                  tempError = new Error("PostOffice or PackingStation not allowed as invoice address");
                  break;
                case isPhoneNumberNeeded:
                  tempError = new Error("Phone number required");
                  break;
                case isDeliveryAddressNotValid:
                  tempError = new Error("Shipping line1 format is invalid.");
                  break;
                case isInvoiceAddressNotValid:
                  tempError = new Error("Billing line1 format is invalid.");
                  break;
                default:
              }

              if (tempError !== null) {
                purchaseButtonErrors(tempError, "Amazon Pay Purchase preautorize (before redir)");

                return;
              }

              /* CAUTION! */
              /* After this, a click on the APay button starts the redirect to Apay page! After confirmation, Apay
               * redirects back to this current page (above mentioned successUrl/failureUrl, plus url param) and after
               * react reload code ends up back in summary view, in initial useEffect (empty dependency array)
               */
              window.confirmationFlow = confirmationFlow;
              setIsAmazonPayButtonEnabled(true);
            } catch (e) {
              purchaseButtonErrors(e, "Amazon Pay Purchase preautorize (before redir)");
            }
          },
        );
      })
      .catch((err) => {
        purchaseButtonErrors(err, "Amazon Pay Purchase init Confirmation Flow execution");
      });
  }, [paymentModule, props.isVisible]);

  /**
   * re-init on-enter-summary procedure on cart value change or voucher change
   */
  useEffect(() => {
    if (!props.isVisible) {
      return;
    }

    const {cartAmount, voucherPosition} = previousValuesRef.current;
    const newCartAmount = cartData.value.payable.amount;
    const newVoucherPos = cartData.positions.find((pos) => pos.type === "Voucher" && pos.reference?.voucher);

    const amountChanged = cartAmount !== newCartAmount;
    const voucherChanged = voucherPosition !== newVoucherPos;
    const isPaypal = paymentId === Payments.PaypalBraintree;

    if ((amountChanged || voucherChanged) && cartData.isValid && isPaypal && paymentModule) {
      try {
        void paymentModule.onEnterSummary();
      } catch (err) {
        if (
          (err as Error).name.startsWith(INTERNAL_ERROR_TYPE.PAYPAL.OnError) ||
          (err as Error).name.startsWith(INTERNAL_ERROR_TYPE.PAYPAL.OnErrorNoHandle)
        ) {
          return;
        }
        const tempError = new Error("Enter Summary: " + (err as Error).message);
        tempError.stack = (err as Error).stack;
        tempError.name = INTERNAL_ERROR_TYPE.PAYPAL.OnReEnter + (err as Error).name;
        purchaseButtonErrors(tempError, "on cart change in Summary");
      }
    }

    previousValuesRef.current = {
      cartAmount: newCartAmount,
      voucherPosition: newVoucherPos,
    };
  }, [props.isVisible, cartData]);

  /**
   * do purchase after apay redirect, or error handling
   */
  useEffect(() => {
    /*
     * Initially check for Apay 'AuthenticationStatus=Success' after redirect and if
     * - no 'error' url param is given
     * - not amazonPayAuthorized, to prevent double auth caused by double rendering
     * - positions available, to prevent re-purchase of new/empty cart, because of unintendedly persistent url params
     * - payment is not authorized, and not-not-inited
     * (else-if-error handling below)
     * (subject to refactoring)
     */
    const urlSearchParams = new URLSearchParams(window.location.search);
    if (
      urlSearchParams.get("AuthenticationStatus") === "Success" &&
      urlSearchParams.get("error") !== btoaError &&
      !window.amazonPayAuthorized &&
      cartData.positions.find((pos) => pos.reference && pos.reference.itemId !== null) &&
      checkout.messages.find((msg) => msg.code === "PAYMENT_NOT_AUTHORIZED")
    ) {
      window.amazonPayAuthorized = true;
      /* Type safety, should never be the case */
      if (!checkout.paymentTransaction) {
        purchaseButtonErrors(
          new Error("No paymentTransaction but AuthenticationStatus=Success"),
          "Amazon Pay Purchase after redirect",
        );

        return;
      }

      /* Authorize */
      PaymentController.authorizePayment()
        .then(async () => {
          await new Promise((resolve) => setTimeout(resolve, 1000));

          /* When everything is fine, do the purchase */
          await checkout.purchase();
        })
        .catch((err: Error) => {
          cleanUrl();
          const tempError = new Error("APay Auth: " + err.message);
          tempError.stack = err.stack;
          tempError.name = err.name;
          purchaseButtonErrors(tempError, "APay Purchase (after redir)");
        });
    } else if (urlSearchParams.get("error") === btoaError) {
      purchaseButtonErrors(
        new Error("APay initConfirmationFlow callback error (after redir)"),
        "useEffect handling urlparams redirecting",
      );
      cleanUrl();
    }
  }, []);

  // region insanely large button logic

  const ExtButton: React.FC<{submitted: boolean; onPurchase: () => void}> = (extButtonProps) => (
    <Button
      action={extButtonProps.onPurchase}
      styles={[ButtonStyles.wide]}
      type={extButtonProps.submitted ? ButtonType.disabled : ButtonType.default}
    >
      {extButtonProps.submitted ? <Spinner /> : buttonText}
    </Button>
  );

  /* PAYPAL EXPRESS */
  const PaypalExpressSummaryButton: React.FC = () => {
    const [submitted, setSubmitted] = useState(false);
    const onPurchase = async () => {
      setSubmitted(true);
      try {
        if (window.annotationTextArea !== (checkout.customerRemark ?? undefined)) {
          await checkout.put({customerRemark: window.annotationTextArea});
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        await PaymentController.initPayment();
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await PaymentController.authorizePayment(window.pp_nonce);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await checkout.purchase();
      } catch (err) {
        purchaseButtonErrors(err, "Paypal Express Purchase Button", {
          ppNonceExists: !!window.pp_nonce,
        });
      }
    };

    return <ExtButton onPurchase={onPurchase} submitted={submitted} />;
  };

  /* AMAZON PAY */
  const AmazonPayButton: React.FC = () => {
    const onPurchase = () => {
      setIsAmazonPayButtonEnabled(false);
      try {
        /* CAUTION! */
        /* This starts the redirect to Apay page! After confirmation, Apay
         * redirects back to this current page (above mentioned successUrl/failureUrl, plus url param) and after
         * react reload code ends up back in summary view, in initial useEffect (empty dependency array)
         */
        if (!errors.length) {
          window.confirmationFlow.success();
        } else {
          window.confirmationFlow.error();
        }
      } catch (err) {
        purchaseButtonErrors(err, "Amazon Pay Purchase init Confirmation Flow execution");
      }
      setIsAmazonPayButtonEnabled(true);
    };

    return <ExtButton onPurchase={onPurchase} submitted={!isAmazonPayButtonEnabled} />;
  };

  /* DEFAULT/INTERNAL PAYMENT */
  const DefaultButton: React.FC = () =>
    paymentModule ? (
      <paymentModule.PurchaseButton
        gotoHandling={() => checkoutView.gotoStep(CheckoutStep.PAYMENT)}
        label={buttonText}
        handleError={purchaseButtonErrors}
      />
    ) : null;

  /* FALLBACK */
  const FallbackButton: React.FC = () => (
    <Button type={ButtonType.disabled} styles={[ButtonStyles.wide]}>
      {t("Ein Fehler ist aufgetreten")}
    </Button>
  );

  const purchaseButtonMemoized = React.useMemo(() => {
    let ButtonToReturn = FallbackButton;

    switch (true) {
      case !checkout.cartData.isValid:
        break;
      case paymentId === Payments.PaypalExpressBraintree:
        ButtonToReturn = PaypalExpressSummaryButton;
        break;

      case paymentId === Payments.AmazonPay:
        ButtonToReturn = AmazonPayButton;
        break;

      case paymentModule !== null:
        ButtonToReturn = DefaultButton;
        break;
    }

    return <ButtonToReturn />;
  }, [paymentId, paymentModule, props.isVisible, checkout.cartData.isValid, isAmazonPayButtonEnabled]);

  // endregion

  /** visibility check */
  if (!props.isVisible) {
    setErrors([]);

    return null;
  }

  if (new URLSearchParams(window.location.search).get("AuthenticationStatus") === "Success" && !errors.length) {
    if (checkout.isCompleted) {
      return null;
    } else {
      return (
        <div className="Shipping Mobile-Only-Friendly">
          <ListLoader />
        </div>
      );
    }
  }

  if (errors.length) {
    const sections = document.querySelectorAll("#ModalRootElement > section");
    if (sections.length) {
      sections.forEach((section) => (section.scrollTop = 0));
    }
  }

  return (
    <div className={`${styles.wrapper} reduced-width`}>
      <h2>{t("Prüfen Sie Ihre Bestellung")}</h2>
      <CheckoutError>{errors}</CheckoutError>
      <CartDetails reduced />
      <SummaryShippingOption goToShippingView={() => checkoutView.gotoStep(CheckoutStep.SHIPPING)} />
      <h2>{t("Ihr Warenkorb", {count: itemPosCount})}</h2>
      <ItemList
        editable={false}
        goToCartView={() => {
          setIsIntoCheckout(false);
          updateUrlParams("cart");
        }}
      />
      <SummaryAddress
        goToAddressView={() => checkoutView.gotoStep(CheckoutStep.ADDRESS)}
        onError={purchaseButtonErrors}
      />
      <SummaryPaymentOption
        goToPaymentView={() => checkoutView.gotoStep(CheckoutStep.PAYMENT)}
        onError={purchaseButtonErrors}
      />
      {isCartManipulationAllowed && <VoucherBlock />}
      <AnnotationBlock />
      <SummaryNewsletter />
      <CartDetails />
      {purchaseButtonMemoized}
      <p className={styles.disclaimer}>
        <Trans i18nKey={"disclaimer"} ns="checkout">
          Mit der Bestellung erklären Sie sich mit unseren
          <a href={t("agb-link")} target="_blank" rel="noreferrer">
            AGB
          </a>
          und
          <a href={t("privacy-link")} target="_blank" rel="noreferrer">
            Datenschutzbestimmungen
          </a>
          einverstanden. Hier finden Sie Informationen zum
          <a href={t("withdrawal-link")} target="_blank" rel="noreferrer">
            Widerrufsrecht
          </a>
          .
        </Trans>
      </p>
    </div>
  );
};

export default React.memo(Summary);

export interface IBraintreeError {
  code: string;
  message: string;
  type: BraintreeErrorTypes;
  details: {
    originalError: {
      code: string;
      message: string;
      type: BraintreeErrorTypes;
      details: {
        originalError?: {
          error: Error;
        };
      };
      error: Error;
    };
  };
}
